import { useState } from 'react';
import { isApiError } from '@/services/api';
import { Alert, Button, Checkbox, Modal, SearchBar } from '@shared/components';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useAttachProductsMutation } from '../hooks/useAttachProductsMutation';
import { useScopedProductsQuery } from '../hooks/useScopedProductsQuery';

const MAX_ATTACH = 200;

interface Props {
  groupId: string;
  ownerDealerId: string;
  attachedProductIds: string[];
  onClose: () => void;
}

export function AttachProductsModal({ groupId, ownerDealerId, attachedProductIds, onClose }: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [outOfScopeIds, setOutOfScopeIds] = useState<string[]>([]);

  const debouncedSearch = useDebounce(search, 300);
  const productsQuery = useScopedProductsQuery(ownerDealerId, debouncedSearch);
  const products = productsQuery.data?.data ?? [];

  const attachMutation = useAttachProductsMutation(groupId);

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const overCap = selected.length > MAX_ATTACH;

  const handleConfirm = () => {
    if (overCap || selected.length === 0) return;
    setOutOfScopeIds([]);
    attachMutation.mutate(selected, {
      onSuccess: () => {
        setSelected([]);
        onClose();
      },
      onError: (err) => {
        if (!isApiError(err) || err.status !== 422) return;
        const body = err.data as { out_of_scope_ids?: unknown } | undefined;
        if (Array.isArray(body?.out_of_scope_ids)) {
          setOutOfScopeIds(body.out_of_scope_ids as string[]);
        }
      },
    });
  };

  return (
    <Modal isOpen onClose={onClose} className="max-w-lg mx-4" ariaLabel="Attach Products">
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold">Attach Products</h2>

        <SearchBar
          searchTerm={search}
          setSearchTerm={setSearch}
          placeholder="Search products"
        />

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {productsQuery.isLoading && <p className="text-sm text-mp-muted">Loading...</p>}
          {productsQuery.isError && (
            <p className="text-mp-error text-sm">Unable to load products. You may not have permission to browse products in this hierarchy.</p>
          )}
          {!productsQuery.isLoading && !productsQuery.isError && products.length === 0 && (
            <p className="text-sm text-mp-muted">No products found.</p>
          )}
          {products.map((product) => {
            const alreadyAttached = attachedProductIds.includes(product.id);
            const isChecked = selected.includes(product.id) || alreadyAttached;
            return (
              <div key={product.id} className="flex items-center gap-2">
                <Checkbox
                  id={`product-${product.id}`}
                  checked={isChecked}
                  onChange={() => !alreadyAttached && toggle(product.id)}
                  disabled={alreadyAttached || attachMutation.isPending}
                  ariaLabel={product.name}
                />
                <span className="text-sm">
                  {product.name}
                  {alreadyAttached && <span className="text-mp-muted ml-1">(already attached)</span>}
                </span>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-mp-muted">
          Selected: {selected.length} / {MAX_ATTACH}
        </p>

        {overCap && (
          <p className="text-mp-error text-xs">
            You can attach a maximum of {MAX_ATTACH} products at a time.
          </p>
        )}

        {outOfScopeIds.length > 0 && (
          <Alert variant="warning" role="note" className="text-xs">
            <div>
              <p className="font-semibold">
                {outOfScopeIds.length} product{outOfScopeIds.length > 1 ? 's are' : ' is'} outside this security group&apos;s dealer scope and could not be attached.
              </p>
              <p className="mt-1 text-mp-muted">IDs: {outOfScopeIds.join(', ')}</p>
            </div>
          </Alert>
        )}

        {attachMutation.isError && outOfScopeIds.length === 0 && (
          <p className="text-mp-error text-xs">Failed to attach products. Please try again.</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={attachMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleConfirm}
            disabled={selected.length === 0 || overCap || attachMutation.isPending}
          >
            {attachMutation.isPending ? 'Attaching...' : 'Attach'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
